import React, { useEffect, useRef, useState } from 'react';
import SeoHelmet from '../components/SeoHelmet';
import VideoSource from "../public/hero-section-video.mp4";
import KahloVideo from "../public/kah-lo.mp4";


const Home = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const ctaRef = useRef(null);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const videos = [
    { src: VideoSource, id: 'hero-video' },
    { src: KahloVideo, id: 'kahlo-video' }
  ];

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
  }, []);

  const switchToNextVideo = () => {
    setFadeOut(true);
    
    setTimeout(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
      setFadeOut(false);
      setVideoLoaded(false);
    }, 500);
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  // Handle user interaction to enable video play
  const handleUserInteraction = () => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
      
      // Try to play video after user interaction
      if (videoRef.current) {
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log("Video play failed:", error);
          });
        }
      }
    }
  };

  useEffect(() => {
    const videoInterval = setInterval(() => {
      if (hasUserInteracted || !isMobile) {
        switchToNextVideo();
      }
    }, 10000);

    return () => clearInterval(videoInterval);
  }, [hasUserInteracted, isMobile]);

  useEffect(() => {
    const animateElements = () => {
      if (titleRef.current) {
        titleRef.current.style.transform = 'translateY(0)';
        titleRef.current.style.opacity = '1';
      }
      if (subtitleRef.current) {
        setTimeout(() => {
          subtitleRef.current.style.transform = 'translateY(0)';
          subtitleRef.current.style.opacity = '1';
        }, 300);
      }
      if (ctaRef.current) {
        setTimeout(() => {
          ctaRef.current.style.transform = 'translateY(0)';
          ctaRef.current.style.opacity = '1';
        }, 600);
      }
    };

    setTimeout(animateElements, 100);
  }, []);

  // Add event listeners for user interaction
  useEffect(() => {
    const enableVideoPlay = () => handleUserInteraction();
    
    // Listen for various user interactions
    const events = ['click', 'touchstart', 'scroll', 'keydown'];
    
    events.forEach(event => {
      document.addEventListener(event, enableVideoPlay, { once: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, enableVideoPlay);
      });
    };
  }, []);

  // Icon components
  const MusicIcon = () => (
    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
    </svg>
  );

  const DiscIcon = () => (
    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  const LocationIcon = () => (
    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const CalendarIcon = () => (
    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );

  const GlobeIcon = () => (
    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const ArtistIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
    </svg>
  );

  const ProjectIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );

  const ServiceIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const PressIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9m0 0l2 2M9 5l2-2" />
    </svg>
  );


  // Fallback content for mobile
  const MobileFallback = () => (
    <div className="absolute inset-0 bg-cover bg-center">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
          filter: 'brightness(0.3)'
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
      </div>
      
      {/* Play button overlay for mobile */}
      {!hasUserInteracted && isMobile && (
        <div 
          className="absolute inset-0 flex items-center justify-center z-30 bg-black/70"
          onClick={handleUserInteraction}
        >
          <div className="text-center p-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all duration-300 transform hover:scale-110">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="text-white text-lg font-medium">Tap to play background video</p>
            <p className="text-gray-300 mt-2">Experience the full HI-WAY 89 journey</p>
          </div>
        </div>
      )}
      
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-purple-900/20"></div>
    </div>
  );

  return (
    <>
      <SeoHelmet
        title="HI-WAY 89 | Independent Record Label | Canada"
        description="Good Music Needs No Permission to Cross Borders. It just needs the right platform. At Hi-Way 89 Entertainment, we bridge Canadian and African sounds with the world."
        canonical="/"
        keywords="record label, music label, Canadian artists, African music, cross-cultural music, global hits, Hi-Way 89, artist development, music production"
      />
      
      <div className="bg-black">
        {/* Hero Section */}
        <div 
          ref={heroRef} 
          className="relative min-h-screen bg-black overflow-hidden max-sm:pt-20"
          onClick={handleUserInteraction}
        >
          <div className="absolute inset-0">
            {/* Video element with mobile optimizations */}
            <video
              ref={videoRef}
              key={videos[currentVideoIndex].id}
              autoPlay={!isMobile || hasUserInteracted}
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ 
                filter: 'brightness(0.6) contrast(1.1)',
                opacity: (videoLoaded && !fadeOut && (!isMobile || hasUserInteracted)) ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out'
              }}
              onLoadedData={handleVideoLoad}
              preload="auto"
              // Important for iOS
              playsinline="true"
              webkit-playsinline="true"
            >
              <source src={videos[currentVideoIndex].src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Fallback for mobile or when video not loaded */}
            {(isMobile && !hasUserInteracted) || !videoLoaded ? (
              <MobileFallback />
            ) : null}
            
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-purple-900/20"></div>
            
        
          </div>

          <div className="relative z-10 flex items-center justify-center min-h-screen px-6 max-sm:mt-10">
            <div className="text-center max-w-4xl mx-auto">
              <div 
                ref={titleRef}
                className="transform translate-y-8 opacity-0 transition-all duration-1000 ease-out mt-28"
              >
                <h1 className="text-5xl max-sm:text-xl md:text-7xl lg:text-8xl font-black text-white mb-4 tracking-tighter">
                  HI-WAY
                  <span className="block text-4xl md:text-5xl lg:text-6xl font-light tracking-wider mt-2">
                    89
                  </span>
                  <span className="text-xl max-sm:text-xl md:text-7xl lg:text-5xl font-black text-white mb-4 tracking-tighter">
                    ENTERTAINMENT
                  </span>
                </h1>
              </div>

              <div 
                ref={subtitleRef}
                className="transform translate-y-8 opacity-0 transition-all duration-1000 ease-out"
              >
                <p className="text-5xl max-sm:text-xl md:text-5xl lg:text-4xl text-gray-300 mb-8 leading-relaxed">
                  Good Music Needs No Permission to Cross Borders.
                </p>
                <p className="text-lg md:text-xl lg:text-3xl text-gray-300 mb-8 font-light leading-relaxed max-w-2xl mx-auto">
                  It just needs the right platform.
                </p>
                <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-8 font-light leading-relaxed max-w-2xl mx-auto">
                  At Hi-Way 89 Entertainment, we bridge Canadian and African sounds with the world. We work with artists who refuse to be boxed in, spotlighting talent from everywhere and turning them into global stars.
                </p>
                <div className="w-16 h-px bg-white mx-auto mb-6"></div>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm md:text-base text-gray-400 uppercase tracking-widest font-medium">
                  <div className="flex items-center">
                    <CalendarIcon />
                    EST. 2025
                  </div>
                </div>
              </div>

              <div 
                ref={ctaRef}
                className="transform translate-y-8 opacity-0 transition-all duration-1000 ease-out mt-12"
              >
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                  <a 
                    href="/artists" 
                    className="flex items-center justify-center px-8 py-4 bg-white text-black font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl"
                    onClick={handleUserInteraction}
                  >
                    <MusicIcon />
                    DISCOVER ARTISTS
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards Section */}
        <section className="py-20 px-6 bg-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl max-sm:text-xl md:text-5xl font-black text-white mb-4">
                EXPLORE THE LABEL
              </h2>
              <div className="w-20 h-1 bg-white mx-auto"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              <a href='/roster' className="group relative bg-white text-black p-8 rounded-none transform transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer border-2 border-white">
                <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-100 transition-opacity duration-300">
                  <ArtistIcon />
                </div>
                <h3 className="text-2xl font-black mb-4 tracking-tight">ROSTER</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Meet our groundbreaking artists shaping the future of sound.
                </p>
                <div className="flex items-center text-sm font-bold uppercase tracking-widest">
                  <span>VIEW ARTISTS</span>
                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </a>

              <a href='/services' className="group relative bg-white text-black p-8 rounded-none transform transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer border-2 border-white">
                <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-100 transition-opacity duration-300">
                  <ServiceIcon />
                </div>
                <h3 className="text-2xl font-black mb-4 tracking-tight">SERVICES</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Full-spectrum music production and artist development.
                </p>
                <div className="flex items-center text-sm font-bold uppercase tracking-widest">
                  <span>OUR OFFERINGS</span>
                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 px-6 bg-white text-black">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl max-sm:text-xl md:text-5xl font-black mb-6 tracking-tight">
                  THE ROAD TO GREATNESS
                </h2>
                <div className="w-20 h-1 bg-black mb-8"></div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  HI-WAY 89 isn't just a label—it's a movement. Born from the intersection of raw talent and relentless innovation, we're paving new roads in the music industry.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  Based in the heart of Canada, we discover and develop artists who dare to be different. Our approach combines traditional artistry with cutting-edge production, creating sounds that resonate across generations.
                </p>
                <button 
                  className="px-8 py-4 bg-black text-white font-bold text-lg hover:bg-gray-900 transition-all duration-300 transform hover:scale-105"
                  onClick={handleUserInteraction}
                >
                  OUR STORY
                </button>
              </div>

              <div className="relative">
                <div className="aspect-square bg-gray-900 relative overflow-hidden border-8 border-black">
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-90"
                    style={{
                      backgroundImage: `url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`
                    }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-12 right-6">
                    <h3 className="text-white text-2xl font-black mb-2">THE JOURNEY BEGINS</h3>
                    <p className="text-gray-300">Est. 2025 in Canada</p>
                  </div>
                </div>
                
                <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-black bg-white"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 border-2 border-black bg-black"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Press Section */}
        <section className="py-20 px-6 bg-black text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
                IN THE PRESS
              </h2>
              <div className="w-20 h-1 bg-white mx-auto"></div>
              <p className="text-gray-300 mt-6 max-w-2xl mx-auto">
                Featured in leading publications and recognized by industry leaders worldwide
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">

              <div className="group relative bg-white text-black overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300 md:col-span-2">
                <div className="h-64 md:h-80 overflow-hidden">
                  <img 
                    src="https://media.cnn.com/api/v1/images/stellar/prod/still-22114264-2681-729-still.jpg?c=16x9&q=w_1280,c_fill"
                    alt="Hi-Way 89 Founders Ikenna Nwagboso and Camillo Doregos"
                    className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                
                    <span className="text-sm font-bold bg-black text-white px-3 py-1">CNN</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-4 leading-tight">
                   How Nigerian music executives are turning Canada into Africa's next global gateway
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    "With Africa’s sound driving global pop culture, Hi-Way 89 Entertainment is positioning Canada as a launchpad for the next generation of African and diaspora artists. "
                  </p>
                  <div className="flex items-center justify-between">
                   
                    <a 
                      href="https://edition.cnn.com/african-voices/nigerian-music-executives-canada-afrobeats-global-gateway"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-black hover:border-gray-600 transition-colors duration-300"
                    >
                      READ FULL ARTICLE
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="group relative bg-white text-black overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300 md:col-span-2">
                <div className="h-64 md:h-80 overflow-hidden">
                  <img 
                    src="https://image.okayafrica.com/113622.webp?imageId=113622&width=2116&height=1412&format=webp"
                    alt="Hi-Way 89 Founders Ikenna Nwagboso and Camillo Doregos"
                    className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold tracking-widest text-gray-500">JULY 17, 2025</span>
                    <span className="text-sm font-bold bg-black text-white px-3 py-1">OKAYAFRICA</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-4 leading-tight">
                    Canadian Music Label Hi-Way 89 Connects African Diaspora Artists with Global Success
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    "The new label, founded by Nigerian-born executives Ikenna Nwagboso and Camillo Doregos, is positioning Canada as a hub for talents in the Afrobeats and Black diaspora music space."
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      <span className="text-gray-500">Featured: </span>
                      <span className="font-bold">Label Launch & Artist Development</span>
                    </div>
                    <a 
                      href="https://www.okayafrica.com/canadian-music-label-hi-way-89-connects-african-diaspora-artists-with-global-success/113480"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-black hover:border-gray-600 transition-colors duration-300"
                    >
                      READ FULL ARTICLE
                    </a>
                  </div>
                </div>
              </div>

              <div className="group relative bg-white text-black overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300 md:col-span-2">
                <div className="h-64 md:h-80 overflow-hidden">
                  <img 
                    src="https://media.cnn.com/api/v1/images/stellar/prod/still-22114258-109064-still.jpg?c=original&q=w_860,c_fill/f_webp"
                    alt="Hi-Way 89 Founders Ikenna Nwagboso and Camillo Doregos"
                    className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                   <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold tracking-widest text-gray-500">Jan 12, 2026</span>
                    <span className="text-sm font-bold bg-black text-white px-3 py-1">CNN</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-4 leading-tight">
                   Inside the Japa generation’s music ambition: How Nigerians in Canada are rewriting the rules of the industry
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    "Toronto — When Nigerians talk about “Japa” — Yoruba slang for escape — they’re referring to the growing wave of young people seeking opportunities abroad. In Canada, especially in Toronto, that movement is fueling a vibrant music scene linking Lagos, Nigeria, with Toronto."
                  </p>
                  <div className="flex items-center justify-between">
                   
                    <a 
                      href="https://edition.cnn.com/world/nigeria-japa-generation-music-canada-spc"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-black hover:border-gray-600 transition-colors duration-300"
                    >
                      READ FULL ARTICLE
                    </a>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-yellow-900 to-amber-900 text-white overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-xs font-bold tracking-widest text-yellow-300">MAY 9, 2024</span>
                      <span className="text-sm font-bold bg-yellow-500 text-black px-3 py-1 rounded">GRAMMY.COM</span>
                    </div>
                    <h3 className="text-xl font-black mb-4 leading-tight">
                      Meet Usher Collaborator Pheelz, The Nigerian Producer & Singer
                    </h3>
                    <p className="text-yellow-100 text-sm mb-6 leading-relaxed">
                      "After working with Usher on two tracks for his latest album, 'Coming Home,' Lagos' Pheelz is looking inward."
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm">
                      <span className="font-bold">Artist Feature</span>
                    </div>
                    <a 
                      href="https://www.grammy.com/news/pheelz-interview-new-ep-album-working-with-usher-videos"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-white hover:border-yellow-400 transition-colors duration-300"
                    >
                      READ INTERVIEW
                    </a>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-red-900 to-black text-white overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-xs font-bold tracking-widest text-gray-400">APRIL 23, 2025</span>
                      <span className="text-sm font-bold bg-red-600 text-white px-3 py-1">BILLBOARD</span>
                    </div>
                    <h3 className="text-xl font-black mb-4 leading-tight">
                      Hi-Way 89 Entertainment Launches in Canada
                    </h3>
                    <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                      Les figures de proue de la musique nigériane inaugurent Hi-Way 89 Entertainment au Canada.
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm">
                      <span className="font-bold">Industry Announcement</span>
                    </div>
                    <a 
                      href="https://ca.billboard.com/tag/hi-way-89"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-white hover:border-red-400 transition-colors duration-300"
                    >
                      VIEW ON BILLBOARD
                    </a>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-purple-900 to-indigo-900 text-white overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-xs font-bold tracking-widest text-purple-300">JULY 30, 2025</span>
                      <span className="text-sm font-bold bg-purple-600 text-white px-3 py-1">SOCAN</span>
                    </div>
                    <h3 className="text-xl font-black mb-4 leading-tight">
                      How Chrissy Spratt Earned 4M+ TikTok Followers
                    </h3>
                    <p className="text-purple-100 text-sm mb-6 leading-relaxed">
                      Ottawa's Chrissy Spratt garnered over 6M followers with her Afrobeats covers.
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm">
                      <span className="font-bold">Success Story</span>
                    </div>
                    <a 
                      href="https://www.socanmagazine.ca/features/how-did-chrissy-spratt-earn-more-than-four-million-tiktok-views/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-white hover:border-purple-400 transition-colors duration-300"
                    >
                      READ ARTICLE
                    </a>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-pink-900 via-purple-900 to-pink-800 text-white overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-bold tracking-widest text-pink-300">SOCIAL MEDIA</span>
                    <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full">INSTAGRAM</span>
                  </div>
                  <h3 className="text-xl font-black mb-4 leading-tight">
                    Follow Our Journey @hiway89ent
                  </h3>
                  <p className="text-pink-100 text-sm mb-6 leading-relaxed">
                    Join our growing community for behind-the-scenes content, artist updates, and industry insights.
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold">@</span>
                      </div>
                      <span className="text-sm font-bold">hiway89ent</span>
                    </div>
                    <a 
                      href="https://www.instagram.com/hiway89ent/?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-white hover:border-pink-400 transition-colors duration-300"
                    >
                      FOLLOW US
                    </a>
                  </div>
                </div>
              </div>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              
              <div className="group relative bg-white text-black overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-all duration-300 md:col-span-2">
                <div className="h-64 md:h-72 overflow-hidden">
                  <img 
                    src="https://hungermag.com/_next/image?url=https%3A%2F%2Fadmin.hungermag.com%2Fapp%2Fuploads%2F2025%2F11%2FHero-Image-6.png&w=1200&q=75"
                    alt="Hi-Way 89 Founders Ikenna Nwagboso and Camillo Doregos"
                    className="w-full h-fulL group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold tracking-widest text-gray-500">November 13, 2025</span>
                    <span className="text-sm font-bold bg-black text-white px-3 py-1">INTERVIEW</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-4 leading-tight">
                    Kah-Lo is done hiding behind the anthems
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    From demanding attention through green wigs to finding her true voice, the Grammy-nominated
                  </p>
                  <div className="flex items-end justify-end">
                    <a 
                      href="https://hungermag.com/interview/kah-lo-is-done-hiding-behind-the-anthems"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold border-b-2 border-black hover:border-gray-600 transition-colors duration-300"
                    >
                      READ FULL ARTICLE
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Home;